/**
 * Geometrie de la colonne de comptes des cards de synchro.
 *
 * POURQUOI CES CHIFFRES VIVENT DANS UN MODULE, ET PAS EN DUR DANS LE JSX.
 * Le plafond de comptes visibles et la hauteur du conteneur sont deux moities
 * d'UNE SEULE contrainte : si la colonne ne peut pas loger `MAX_VISIBLE + 1`
 * pastilles, la pastille coupee est celle du bas, c'est-a-dire le compteur
 * « +N comptes » — l'unique element qui SIGNALE l'existence des comptes
 * caches. Le bug se presente donc comme une disparition silencieuse : un
 * utilisateur a 15 comptes en voyait 4, sans aucune trace des 11 autres.
 *
 * Separes, les deux nombres derivent l'un de l'autre sans que rien ne le dise.
 * Reunis ici et verifies par `tradovateBeamLayout.test.ts`, une retouche de
 * hauteur qui reintroduirait le rognage fait echouer un test.
 *
 * MODULE PUR : aucun import React ni `wasp/*`, pour rester testable.
 */

/** Hauteur d'une pastille de compte (`h-7` en Tailwind). */
export const BEAM_CHIP_HEIGHT_PX = 28;

/** Espacement vertical entre deux pastilles (`gap-1.5`). */
export const BEAM_CHIP_GAP_PX = 6;

/**
 * Largeur d'une pastille de compte, en pixels.
 *
 * Elle sert DEUX FOIS : a dimensionner la pastille, et a calculer le decalage
 * qui fait arriver le faisceau sur son BORD GAUCHE plutot qu'en son centre
 * (`AnimatedBeam` ancre par defaut au centre, cf. `rectB.width / 2`). Les deux
 * doivent donc bouger ensemble, d'ou la constante partagee : une largeur
 * changee d'un seul cote ferait arriver le trait au milieu du texte.
 */
export const BEAM_CHIP_WIDTH_PX = 150;

/** Idem au-dela du point de rupture `sm`. */
export const BEAM_CHIP_WIDTH_SM_PX = 168;

/** Marge verticale du conteneur (`py-4` : 16 px en haut + 16 px en bas). */
export const BEAM_CONTAINER_VERTICAL_PADDING_PX = 32;

/**
 * Comptes affiches avant repli sur le compteur.
 *
 * Demande produit : « on met les 5 premiers et si il y en a +, on ecrit
 * "+X comptes" ». Cinq comptes TOUJOURS visibles, donc une card dimensionnee
 * pour cinq slots au minimum.
 */
export const MAX_VISIBLE_ACCOUNTS = 5;

/**
 * Nombre maximal de pastilles empilees : les comptes visibles PLUS le
 * compteur de debordement, qui est une pastille comme les autres.
 */
export const MAX_BEAM_NODES = MAX_VISIBLE_ACCOUNTS + 1;

/**
 * Hauteur minimale du conteneur pour loger la pile complete sans rognage.
 *
 * 6 pastilles = 6 x 28 px + 5 x 6 px = 198 px de contenu, plus 32 px de
 * marges verticales = 230 px.
 */
export const MIN_BEAM_CONTAINER_HEIGHT_PX =
  MAX_BEAM_NODES * BEAM_CHIP_HEIGHT_PX +
  (MAX_BEAM_NODES - 1) * BEAM_CHIP_GAP_PX +
  BEAM_CONTAINER_VERTICAL_PADDING_PX;

/**
 * Hauteur REELLEMENT posee sur le conteneur, en px, et UNIQUE pour toutes les
 * cards.
 *
 * Hauteur unique et non « au contenu » : la grille est a trois cards par
 * ligne, et des cards de hauteurs differentes donneraient une rangee en dents
 * de scie. Une connexion a zero ou un compte respire simplement davantage,
 * ce qui est le compromis accepte.
 *
 * ⚠️ Doit rester >= `MIN_BEAM_CONTAINER_HEIGHT_PX`, sinon la pastille
 * « +N comptes » repasse sous le `overflow-hidden` de la card.
 */
export const BEAM_CONTAINER_HEIGHT_PX = 232;

/**
 * Etalement vertical des faisceaux Edgely -> comptes, en px par rang.
 *
 * `AnimatedBeam` place son point de controle a `startY - curvature` : la
 * `curvature` est donc une FLECHE en pixels, pas un ratio. Elle doit rester
 * proportionnee a la CORDE, qui n'a pas bougé en largeur.
 *
 * ⚠️ POURQUOI ON DESCEND DE 10 A 7. Le multiplicateur s'applique au rang
 * centre `(i - (n-1)/2)`, dont l'amplitude croit avec le nombre de noeuds :
 * a 5 noeuds l'ecart extreme valait ±2 x 10 = ±20 px, a 6 noeuds il vaudrait
 * ±2,5 x 10 = ±25 px. Or les pastilles sont deja PLUS ECARTEES verticalement
 * qu'avant (la colonne est plus haute) : leurs faisceaux divergent donc
 * naturellement davantage, et y ajouter une fleche plus forte les ferait se
 * croiser au-dessus du hub. A 7, l'ecart extreme a 6 noeuds vaut ±17,5 px,
 * soit legerement MOINS que les ±20 px d'avant : la silhouette reste un
 * faisceau, pas une gerbe d'arcs.
 */
export const BEAM_CURVATURE_STEP_PX = 7;
